import type {
  FinancialBillingSelection,
  FinancialCoverage,
  FinancialCustomerSummary,
  FinancialImport,
  FinancialLine,
} from '@/types/Models/Financial'
import { financialAmountsEqual, financialCommonCurrency, subtractFinancialAmounts, sumFinancialAmounts } from '@/services/FinancialAmounts'
import { financialBillingId, financialBillingName, financialChargeAmount, financialLocalDate, financialReady } from '@/services/FinancialImport'
import { financialBilledRowIds, financialBillingStates } from '@/services/FinancialSignals'

// A shipment quote is context, not a price for each fee. Count it once across the selected invoice rows.
export const financialQuotedTotal = (lines: FinancialLine[]): string | null => {
  const quotes = new Map<string, string>()
  for (const { row } of lines) {
    if (row.matchStatus !== 'matched' || !row.match || row.match.bookedPrice === null) return null
    const previous = quotes.get(row.match.itemId)
    if (previous !== undefined && !financialAmountsEqual(previous, row.match.bookedPrice)) return null
    quotes.set(row.match.itemId, row.match.bookedPrice)
  }
  return quotes.size ? sumFinancialAmounts([...quotes.values()]) : null
}

// Michél: every carrier cost should lead to customer revenue. Anything not approved for billing stays visible.
export const financialCoverage = (batch: FinancialImport): FinancialCoverage => {
  const all: string[] = []
  const covered: string[] = []
  const uncovered: string[] = []
  let invalid = false
  const coverage = { ready: 0, pending: 0, excluded: 0, unresolved: 0 }
  for (const row of batch.charges) {
    const amount = financialChargeAmount(batch, row)
    if (amount === null) invalid = true
    else all.push(amount)
    if (financialReady(batch, row)) {
      coverage.ready += 1
      covered.push(amount!)
      continue
    }
    if (amount !== null) uncovered.push(amount)
    if (row.review?.status === 'excluded') coverage.excluded += 1
    else if (row.matchStatus !== 'matched') coverage.unresolved += 1
    else coverage.pending += 1
  }
  return {
    ...coverage,
    total: invalid ? null : sumFinancialAmounts(all),
    covered: sumFinancialAmounts(covered),
    uncovered: invalid ? null : sumFinancialAmounts(uncovered),
  }
}

export const financialImportStatus = (batch: FinancialImport): { label: string; tone: 'review' | 'ready' | 'neutral' } => {
  if (!batch.matchedAt) return { label: 'Not matched', tone: 'neutral' }
  const coverage = financialCoverage(batch)
  if (coverage.pending + coverage.unresolved)
    return { label: `${(coverage.pending + coverage.unresolved).toLocaleString('en-GB')} to review`, tone: 'review' }
  const billed = financialBilledRowIds(batch)
  const unbilled = batch.charges.filter((row) => financialReady(batch, row) && !billed.has(row.id)).length
  if (unbilled) return { label: `${unbilled} ready to bill`, tone: 'ready' }
  return coverage.excluded
    ? { label: `Billed · ${coverage.excluded} excluded without revenue`, tone: 'review' }
    : { label: 'Billed', tone: 'ready' }
}

export const financialCustomers = (batch: FinancialImport): FinancialCustomerSummary[] => {
  const groups = new Map<
    string,
    { summary: FinancialCustomerSummary; lines: FinancialLine[]; amounts: string[]; uncovered: string[]; shipments: Set<string>; invalid: boolean }
  >()
  for (const row of batch.charges) {
    const id = financialBillingId(row.match)
    const group = groups.get(id) ?? {
      summary: {
        id,
        name: financialBillingName(row.match),
        charges: 0,
        shipments: 0,
        amount: '0',
        reviewedRevenue: null,
        reviewedMargin: null,
        uncoveredAmount: '0',
        uncoveredRows: 0,
        ready: 0,
        needsReview: 0,
        excluded: 0,
        unresolved: 0,
      },
      invalid: false,
      lines: [],
      amounts: [],
      uncovered: [],
      shipments: new Set<string>(),
    }
    group.lines.push({ batch, row })
    const amount = financialChargeAmount(batch, row)
    group.summary.charges += 1
    if (financialReady(batch, row)) {
      group.summary.ready += 1
      group.summary.reviewedRevenue = sumFinancialAmounts([group.summary.reviewedRevenue ?? '0', row.review!.customerPrice!])
      group.summary.reviewedMargin = sumFinancialAmounts([
        group.summary.reviewedMargin ?? '0',
        subtractFinancialAmounts(row.review!.customerPrice!, amount!),
      ])
    } else {
      group.summary.uncoveredRows += 1
      if (amount !== null) group.uncovered.push(amount)
      if (row.review?.status === 'excluded') group.summary.excluded += 1
      else group.summary.needsReview += 1
      if (row.matchStatus !== 'matched') group.summary.unresolved += 1
    }
    if (amount !== null) group.amounts.push(amount)
    else group.invalid = true
    if (row.trackingNumber) group.shipments.add(row.trackingNumber)
    groups.set(id, group)
  }
  return [...groups.values()]
    .map(({ summary, lines, amounts, uncovered, shipments, invalid }) => ({
      ...summary,
      quotedPrice: financialQuotedTotal(lines),
      amount: invalid ? null : sumFinancialAmounts(amounts),
      uncoveredAmount: invalid ? null : sumFinancialAmounts(uncovered),
      shipments: shipments.size,
    }))
    .sort((a, b) => b.needsReview - a.needsReview || a.name.localeCompare(b.name))
}

// The cut-off is when the cost became available here, not its older carrier service date. Unbilled rows and
// corrections from earlier imports carry forward, while unchanged billed rows stay inside their selected period.
export const financialCustomerLines = (
  imports: FinancialImport[],
  selection: FinancialBillingSelection,
): {
  ready: FinancialLine[]
  changed: FinancialLine[]
  billed: FinancialLine[]
  open: FinancialLine[]
  excluded: FinancialLine[]
  // Billed earlier and then excluded or reopened: the customer must be credited what was billed.
  credits: FinancialLine[]
} => {
  const result = {
    ready: [] as FinancialLine[],
    changed: [] as FinancialLine[],
    billed: [] as FinancialLine[],
    open: [] as FinancialLine[],
    excluded: [] as FinancialLine[],
    credits: [] as FinancialLine[],
  }
  for (const batch of imports) {
    const states = financialBillingStates(batch)
    const received = financialLocalDate(batch.createdAt)
    if (!received || (selection.to && received > selection.to)) continue
    for (const row of batch.charges) {
      if (!row.match || financialBillingId(row.match) !== selection.customerId) continue
      const state = states.get(row.id) ?? 'none'
      if (selection.from && received < selection.from && state === 'billed') continue
      if (!financialReady(batch, row)) {
        if (state === 'changed') result.credits.push({ batch, row })
        if (row.review?.status === 'excluded') result.excluded.push({ batch, row })
        else result.open.push({ batch, row })
      } else if (state === 'billed') result.billed.push({ batch, row })
      else if (state === 'changed') result.changed.push({ batch, row })
      else result.ready.push({ batch, row })
    }
  }
  return result
}

export const financialLinesTotals = (lines: FinancialLine[]): { cost: string | null; revenue: string | null; margin: string | null } => {
  if (financialCommonCurrency(lines.map((line) => line.batch.currency)) === null)
    return { cost: null, revenue: null, margin: null }
  const costs = lines.map((line) => financialChargeAmount(line.batch, line.row))
  const revenue = sumFinancialAmounts(lines.map((line) => line.row.review?.customerPrice ?? '0'))
  const cost = costs.some((value) => value === null) ? null : sumFinancialAmounts(costs as string[])
  return { cost, revenue, margin: cost === null ? null : subtractFinancialAmounts(revenue, cost) }
}
